import { clsx, getBlockClassname } from '@epilot/journey-logic-commons'
import type { BlockUiSchema, CONTROL_NAME } from '@epilot/journey-logic-commons'
import type { UISchemaElement } from '@jsonforms/core'
import { motion, useReducedMotion } from 'motion/react'
import { forwardRef, type PropsWithChildren, type ReactNode } from 'react'

import { useNavigationContext } from '../../../blocks-renderers/providers'
import {
  NOT_REVEALED_CONTROLS,
  POSITION_LAYOUT_ANIMATION_BLOCK_TYPES
} from '../../../blocks-renderers/utils/hooks/useAnimationEffects'
import { ID_TYPE, uuidToHtmlId } from '../../utils'

export type AnimationType = 'load' | 'visibility' | 'none'

type BlockRevealContainerProps = {
  isVisible: boolean
  animationType: AnimationType
  children: ReactNode
  isActive?: boolean
  className?: string
  blockType?: string
  child?: UISchemaElement
}

export const BlockRevealContainer = forwardRef<
  HTMLDivElement,
  PropsWithChildren<BlockRevealContainerProps>
>((props, ref) => {
  const {
    animationType,
    isActive = false,
    children,
    className,
    blockType,
    child
  } = props
  const { isNavigatingBack } = useNavigationContext()
  const shouldReduceMotion = useReducedMotion()
  const blockClassname = getBlockClassname(blockType as CONTROL_NAME, child)
  const blockHTMLId = uuidToHtmlId(ID_TYPE.BLOCK, (child as BlockUiSchema)?.id)

  const isRevealedControl = !NOT_REVEALED_CONTROLS.includes(
    blockType as CONTROL_NAME
  )

  // isActive is true when we want to control the animation programmatically
  // shouldReduceMotion is true when the user has reduced motion enabled on their device
  const isAnimationDisabled =
    !(isActive && isRevealedControl) || shouldReduceMotion

  const isPositionLayoutAnimation =
    POSITION_LAYOUT_ANIMATION_BLOCK_TYPES.includes(blockType as CONTROL_NAME)

  const layout = isAnimationDisabled
    ? false
    : isPositionLayoutAnimation
      ? 'position'
      : true

  return (
    <motion.div
      animate={{ x: 0, y: 0, opacity: 1 }}
      className={clsx(className, blockClassname)}
      custom={isAnimationDisabled ? 'none' : animationType}
      exit="exit"
      id={blockHTMLId}
      initial="initial"
      layout={layout}
      ref={ref}
      transition={{
        duration: isAnimationDisabled ? 0 : 0.4,
        ease: 'easeInOut',
        type: 'spring',
        // Bounce effect
        stiffness: 300,
        damping: 30,
        layout: {
          type: 'tween'
        }
      }}
      variants={{
        initial: (animationType: AnimationType) => {
          if (animationType === 'load') {
            return {
              x: !isNavigatingBack ? 100 : -100,
              y: 0,
              opacity: 0
            }
          }

          return {
            y: 0,
            x: 0,
            opacity: 0
          }
        },
        exit: (animationType: AnimationType) => {
          if (animationType === 'load') {
            return {
              x: !isNavigatingBack ? -100 : 100,
              y: 0,
              opacity: 0
            }
          }

          return {
            y: 0,
            x: 0,
            opacity: 0
          }
        }
      }}
    >
      {children}
    </motion.div>
  )
})

BlockRevealContainer.displayName = 'BlockRevealContainer'
